import cloneDeep from 'lodash.clonedeep';
import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import { RouteManager } from '../../../RouteManager';

import { BaseApiStore } from '../../../store/BaseApiStore';
import { flowCardActionsStore } from '../../../store/flow-cards/FlowCardActionsStore';
import { flowCardConditionsStore } from '../../../store/flow-cards/FlowCardConditionsStore';
import { flowCardTriggersStore } from '../../../store/flow-cards/FlowCardTriggersStore';
import { FlowCardUtils } from '../../../store/flow-cards/FlowCardUtils';
import { useAttach } from '../../../store/useAttach';

import { useApi } from '../../../store/SelectedHomeyStore.hooks';

const PROGRAMMATIC_TRIGGER_ID = 'homey:manager:flow:programmatic_trigger';

export function getEffectiveFlowTrigger(trigger) {
  if (trigger != null) {
    return trigger;
  }

  return {
    id: PROGRAMMATIC_TRIGGER_ID,
    args: {},
  };
}

/**
 * For now it wont update the store on all flow property updates.
 * todo: convert to subscriber pattern
 * todo: keep track of edits and merge updates
 * todo: prevent updates from rerendering useFlowEditor
 */

class FlowEditor extends BaseApiStore {
  constructor() {
    super('flowEditor');
  }

  createInitialState() {
    return {
      data: null,
      flow: null,
      initialFlow: null,
      history: [],
      historyIndex: 0,
      loading: true,
      saving: false,
      error: null,
    };
  }

  async fetchFlow({ flowId, api }) {
    this.destroy();

    this.set({
      ...this.createInitialState(),
    });

    try {
      const data = await api.flow.getFlow({ id: flowId });
      const flow = cloneDeep(data);

      const conditionGroups = {
        group1: [],
        group2: [],
        group3: [],
      };

      flow.conditions?.forEach((condition) => {
        conditionGroups[condition.group].push(condition);
      });

      flow.conditions = [
        ...conditionGroups.group1,
        ...conditionGroups.group2,
        ...conditionGroups.group3,
      ];

      const actionGroups = {
        then: [],
        else: [],
      };

      flow.actions?.forEach((action) => {
        actionGroups[action.group].push(action);
      });

      flow.actions = [...actionGroups.then, ...actionGroups.else];

      const initialFlow = cloneDeep(flow);

      this.set({
        ...this.createInitialState(),
        loading: false,
        data: data,
        flow: flow,
        initialFlow: initialFlow,
        history: [initialFlow],
        historyIndex: 0,
      });

      data.on('update', this.handleUpdate);
      data.on('delete', this.handleDelete);
    } catch (error) {
      this.destroy();
      console.error(error);

      if (error.code === 404) {
        RouteManager.toFlows();
      }

      this.set({
        ...this.createInitialState(),
        loading: false,
        error,
      });
    }
  }

  handleDelete = () => {
    const state = this.get();

    state.data?.off('update', this.handleUpdate);
    state.data?.off('delete', this.handleDelete);

    this.set({
      ...this.createInitialState(),
    });

    RouteManager.toFlows();
  };

  handleUpdate = (updatedFlow) => {
    const state = this.get();

    // if the name changed and the current edit flow name was not changed
    if (state.flow.name !== updatedFlow.name && state.flow.name === state.initialFlow.name) {
      state.initialFlow.name = updatedFlow.name;

      this.set({
        flow: {
          ...state.flow,
          name: updatedFlow.name,
        },
      });
    }
  };

  setCreateFlow({ folderId }) {
    this.destroy();

    const data = {
      id: 'create',
      name: '',
      folder: folderId ?? null,
      trigger: null,
      conditions: [],
      actions: [],
    };

    const initialFlow = cloneDeep(data);

    this.set({
      ...this.createInitialState(),
      loading: false,
      data: data,
      flow: initialFlow,
      initialFlow: initialFlow,
      history: [initialFlow],
      historyIndex: 0,
    });
  }

  onDetached() {
    this.destroy();
    this.set(this.createInitialState());
  }

  destroy() {
    const state = this.get();

    state.data?.off?.('delete', this.handleDelete);
    state.data?.off?.('update', this.handleUpdate);
  }

  saveFlow() {
    const state = this.get();
    const effectiveTrigger = getEffectiveFlowTrigger(state.flow.trigger);
    const nextFlow = {
      ...state.flow,
      trigger: effectiveTrigger,
    };
    const nextHistory = [...state.history];

    nextHistory[state.historyIndex] = cloneDeep(nextFlow);

    this.set({
      saving: true,
    });

    const waitPromise = new Promise((resolve) => setTimeout(resolve, 300));
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.api.flow.updateFlow({
          id: state.flow.id,
          flow: {
            trigger: effectiveTrigger,
            conditions: state.flow.conditions,
            actions: state.flow.actions,
            name: state.flow.name,
          },
        });
        await waitPromise;

        this.set({
          flow: nextFlow,
          initialFlow: nextFlow,
          history: nextHistory,
          saving: false,
        });

        resolve(result);
      } catch (error) {
        this.set({
          saving: false,
        });

        reject(error);
      }
    });
  }

  createFlow() {
    const state = this.get();
    const effectiveTrigger = getEffectiveFlowTrigger(state.flow.trigger);
    const nextFlow = {
      ...state.flow,
      trigger: effectiveTrigger,
    };
    const nextHistory = [...state.history];

    nextHistory[state.historyIndex] = cloneDeep(nextFlow);

    this.set({
      saving: true,
    });

    const waitPromise = new Promise((resolve) => setTimeout(resolve, 300));
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.api.flow.createFlow({
          flow: {
            trigger: effectiveTrigger,
            conditions: state.flow.conditions,
            actions: state.flow.actions,
            name: state.flow.name,
            folder: state.flow.folder,
          },
        });
        await waitPromise;

        this.set({
          flow: nextFlow,
          initialFlow: nextFlow,
          history: nextHistory,
          saving: false,
        });

        resolve(result);
      } catch (error) {
        this.set({
          saving: false,
        });

        reject(error);
      }
    });
  }

  validate({ type } = {}) {
    const { flow } = this.get();

    const validation = {};
    const flowCardTriggersStoreState = flowCardTriggersStore.get();
    const flowCardConditionsStoreState = flowCardConditionsStore.get();
    const flowCardActionsStoreState = flowCardActionsStore.get();

    if ((flow.name == null || flow.name.length === 0) && type !== 'test') {
      validation.name = {
        type: 'flow.validation.missing_name',
      };
    }

    if (flow.trigger != null) {
      const triggerCard = flowCardTriggersStoreState.byId?.[flow.trigger.id];
      const cardTitle = FlowCardUtils.getTitle(triggerCard);

      if (triggerCard?.droptoken != null && flow.trigger.droptoken === null) {
        validation[`trigger::0::droptoken`] = {
          type: 'flow.validation.missing_droptoken',
          cardTitle: cardTitle,
        };
      }

      Object.entries(flow.trigger.args ?? {}).forEach(([key, value]) => {
        const cardArg = triggerCard?.args?.find((arg) => arg.name === key);
        const isRequired = cardArg?.required ?? true;

        if (value == null && isRequired)
          validation[`trigger::0::${key}`] = {
            type: 'flow.validation.missing_argument',
            cardTitle: cardTitle,
            argTitle: cardArg.title ?? cardArg.type,
          };
      });
    }

    if (flow.conditions.length > 0) {
      flow.conditions.forEach((condition, index) => {
        const conditionCard = flowCardConditionsStoreState.byId?.[condition.id];
        const cardTitle = FlowCardUtils.getTitle(conditionCard);

        if (conditionCard?.droptoken != null && condition.droptoken === null) {
          validation[`condition::${index}::droptoken`] = {
            type: 'flow.validation.missing_droptoken',
            cardTitle: cardTitle,
          };
        }

        Object.entries(condition.args ?? {}).forEach(([key, value]) => {
          const cardArg = conditionCard?.args?.find((arg) => arg.name === key);
          const isRequired = cardArg?.required ?? true;

          if (value == null && isRequired) {
            validation[`action::${index}::${key}`] = {
              type: 'flow.validation.missing_argument',
              cardTitle: cardTitle,
              argTitle: cardArg.title ?? cardArg.type,
            };
          }
        });
      });
    }

    if (flow.actions.length === 0) {
      validation.actions = {
        type: 'flow.validation.missing_action',
      };
    } else {
      flow.actions.forEach((action, index) => {
        const actionCard = flowCardActionsStoreState.byId?.[action.id];
        const cardTitle = FlowCardUtils.getTitle(actionCard);

        if (actionCard?.droptoken != null && action.droptoken === null) {
          validation[`action::${index}::droptoken`] = {
            type: 'flow.validation.missing_droptoken',
            cardTitle: cardTitle,
          };
        }

        Object.entries(action.args ?? {}).forEach(([key, value]) => {
          const cardArg = actionCard?.args?.find((arg) => arg.name === key);
          const isRequired = cardArg?.required ?? true;

          if (value == null && isRequired) {
            validation[`action::${index}::${key}`] = {
              type: 'flow.validation.missing_argument',
              cardTitle: cardTitle,
              argTitle: cardArg.title ?? cardArg.type,
            };
          }
        });
      });
    }

    return validation;
  }

  setNextFlow({ flow, state }) {
    const nextHistory = [...state.history.slice(0, state.historyIndex + 1), cloneDeep(flow)];

    this.set({
      flow: flow,
      history: nextHistory,
      historyIndex: nextHistory.length - 1,
    });
  }

  undo() {
    const state = this.get();

    const flow = state.history[state.historyIndex - 1];
    if (!flow) return;

    const nextHistoryIndex = state.historyIndex - 1;

    this.set({
      flow: cloneDeep(flow),
      historyIndex: nextHistoryIndex,
    });
  }

  redo() {
    const state = this.get();

    const flow = state.history[state.historyIndex + 1];
    if (!flow) return;

    const nextHistoryIndex = state.historyIndex + 1;

    this.set({
      flow: cloneDeep(flow),
      historyIndex: nextHistoryIndex,
    });
  }

  setProperty({ property, value }) {
    const state = this.get();
    const flow = state.flow;

    const nextFlow = {
      ...flow,
      [property]: value,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  setTrigger({ cardKey, cardIndex, card, cardDroptoken }) {
    const state = this.get();
    const cardArgs = card.args ?? [];

    const nextFlow = {
      ...state.flow,
      trigger: {
        ...FlowCardUtils.generateFlowCardDataIdProperties(card),
        droptoken: card.droptoken ? null : undefined,
        args: cardArgs.reduce((accumulator, arg) => {
          accumulator[arg.name] = arg.value ?? undefined;
          return accumulator;
        }, {}),
      },
    };

    if (cardDroptoken != null) {
      nextFlow.trigger.droptoken = cardDroptoken;
    }

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  setCondition({ cardKey, cardIndex, card, cardDroptoken }) {
    const state = this.get();
    const cardArgs = card.args ?? [];

    const nextConditions = [...state.flow.conditions];

    let group;

    if (cardIndex != null) {
      const prevCondition = nextConditions[cardIndex];
      group = prevCondition.group;
    } else {
      const groupNum = nextConditions.reduce((accumulator, condition) => {
        const group = condition.group;
        const groupNum = parseInt(group.replace('group', ''), 10);

        if (groupNum > accumulator) return groupNum;

        return accumulator;
      }, 1);
      group = `group${groupNum}`;
    }

    const nextCondition = {
      ...FlowCardUtils.generateFlowCardDataIdProperties(card),
      group: group,
      droptoken: card.droptoken ? null : undefined,
      inverted: false,
      args: cardArgs.reduce((accumulator, arg) => {
        accumulator[arg.name] = arg.value ?? undefined;
        return accumulator;
      }, {}),
    };

    if (cardDroptoken != null) {
      nextCondition.droptoken = cardDroptoken;
    }

    if (cardIndex != null) {
      nextConditions[cardIndex] = nextCondition;
    } else {
      // todo: find last index based on group
      nextConditions.push(nextCondition);
    }

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  setAction({ cardKey, cardIndex, card, cardDroptoken }) {
    const state = this.get();
    const cardArgs = card.args ?? [];

    const nextActions = [...state.flow.actions];

    let group;

    if (cardIndex != null) {
      const prevAction = nextActions[cardIndex];
      group = prevAction.group;
    } else {
      group = nextActions.reduce((accumulator, action) => {
        const group = action.group;

        if (group === 'else') {
          return 'else';
        }

        return accumulator;
      }, 'then');
    }

    const nextAction = {
      ...FlowCardUtils.generateFlowCardDataIdProperties(card),
      group: group,
      delay: null,
      duration: null,
      droptoken: card.droptoken ? null : undefined,
      args: cardArgs.reduce((accumulator, arg) => {
        accumulator[arg.name] = arg.value ?? undefined;
        return accumulator;
      }, {}),
    };

    if (cardDroptoken != null) {
      nextAction.droptoken = cardDroptoken;
    }

    if (cardIndex != null) {
      nextActions[cardIndex] = nextAction;
    } else {
      // todo: find last index based on group
      nextActions.push(nextAction);
    }

    const nextFlow = {
      ...state.flow,
      actions: nextActions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  setActions({ actions }) {
    const state = this.get();

    const nextFlow = {
      ...state.flow,
      actions: actions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  setConditions({ conditions }) {
    const state = this.get();

    const nextFlow = {
      ...state.flow,
      conditions: conditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateCard({ cardType, cardIndex, argumentKey, value }) {
    switch (cardType) {
      case 'trigger':
        this.updateTriggerCard({ cardIndex, argumentKey, value });
        break;
      case 'condition':
        this.updateConditionCard({ cardIndex, argumentKey, value });
        break;
      case 'action':
        this.updateActionCard({ cardIndex, argumentKey, value });
        break;
      default:
        break;
    }
  }

  updateTriggerCard({ cardIndex, argumentKey, value }) {
    const state = this.get();
    const trigger = state.flow.trigger;
    const nextTrigger = {
      ...trigger,
      args: {
        ...trigger.args,
        [argumentKey]: value,
      },
    };

    const nextFlow = {
      ...state.flow,
      trigger: nextTrigger,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateConditionCard({ cardIndex, argumentKey, value }) {
    const state = this.get();
    const condition = state.flow.conditions[cardIndex];
    const nextCondition = {
      ...condition,
      args: {
        ...condition.args,
        [argumentKey]: value,
      },
    };

    const nextConditions = [...state.flow.conditions];
    nextConditions[cardIndex] = nextCondition;

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateActionCard({ cardIndex, argumentKey, value }) {
    const state = this.get();
    const action = state.flow.actions[cardIndex];
    const nextAction = {
      ...action,
      args: {
        ...action.args,
        [argumentKey]: value,
      },
    };

    const nextActions = [...state.flow.actions];
    nextActions[cardIndex] = nextAction;

    const nextFlow = {
      ...state.flow,
      actions: nextActions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateCardProperty({ cardType, ...rest }) {
    switch (cardType) {
      case 'trigger':
        this.updateTriggerCardProperty(rest);
        break;
      case 'condition':
        this.updateConditionCardProperty(rest);
        break;
      case 'action':
        this.updateActionCardProperty(rest);
        break;
      default:
        break;
    }
  }

  updateTriggerCardProperty({ cardIndex, property, value }) {
    const state = this.get();
    const trigger = state.flow.trigger;
    const nextTrigger = {
      ...trigger,
      [property]: value,
    };

    const nextFlow = {
      ...state.flow,
      trigger: nextTrigger,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateConditionCardProperty({ cardIndex, property, value }) {
    const state = this.get();
    const condition = state.flow.conditions[cardIndex];
    const nextCondition = {
      ...condition,
      [property]: value,
    };

    const nextConditions = [...state.flow.conditions];
    nextConditions[cardIndex] = nextCondition;

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  updateActionCardProperty({ cardIndex, property, value }) {
    const state = this.get();
    const action = state.flow.actions[cardIndex];
    const nextAction = {
      ...action,
      [property]: value,
    };

    const nextActions = [...state.flow.actions];
    nextActions[cardIndex] = nextAction;

    const nextFlow = {
      ...state.flow,
      actions: nextActions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  deleteTriggerCard({ cardIndex }) {
    const state = this.get();

    const nextFlow = {
      ...state.flow,
      trigger: null,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  deleteConditionCard({ cardIndex }) {
    const state = this.get();
    const nextConditions = [...state.flow.conditions];
    nextConditions.splice(cardIndex, 1);

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  deleteActionCard({ cardIndex }) {
    const state = this.get();
    const nextActions = [...state.flow.actions];
    nextActions.splice(cardIndex, 1);

    const nextFlow = {
      ...state.flow,
      actions: nextActions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  duplicateConditionCard({ cardIndex }) {
    const state = this.get();
    const nextConditions = [...state.flow.conditions];
    const condition = state.flow.conditions[cardIndex];

    nextConditions.splice(cardIndex, 0, cloneDeep(condition));

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  duplicateActionCard({ cardIndex }) {
    const state = this.get();
    const nextActions = [...state.flow.actions];
    const action = state.flow.actions[cardIndex];

    nextActions.splice(cardIndex, 0, cloneDeep(action));

    const nextFlow = {
      ...state.flow,
      actions: nextActions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }

  invertConditionCard({ cardIndex }) {
    const state = this.get();
    const condition = state.flow.conditions[cardIndex];
    const nextCondition = {
      ...condition,
      inverted: !condition.inverted,
    };

    const nextConditions = [...state.flow.conditions];
    nextConditions[cardIndex] = nextCondition;

    const nextFlow = {
      ...state.flow,
      conditions: nextConditions,
    };

    this.setNextFlow({
      flow: nextFlow,
      state: state,
    });
  }
}

export const flowEditor = new FlowEditor();

export function useSetFlowEditor({ flowId, folderId, resetId }) {
  useAttach(flowEditor, 'flowEditor');
  const { api } = useApi();

  useEffect(() => {
    if (api != null && flowId != null) {
      if (flowId === 'create') {
        flowEditor.setCreateFlow({ folderId });
      } else {
        flowEditor.fetchFlow({ flowId, api });
      }
    }
  }, [api, flowId, folderId, resetId]);

  useEffect(() => {
    return function () {
      flowEditor.onDetached();
    };
  }, []);
}

export function useFlowEditor() {
  useAttach(flowEditor, 'flowEditor');
  return flowEditor.store(select, shallow);
}

function select(state) {
  return {
    data: state.data,
    flow: state.flow,
    initialFlow: state.initialFlow,
    history: state.history,
    historyIndex: state.historyIndex,
    loading: state.loading,
    saving: state.saving,
    error: state.error,
    manager: state.manager,
    retry: state.retry,
  };
}
